import BaseController from "sap/mcsuite/controller/BaseController";
import ODataModelUtils from "../subtasks/utils/ODataModelUtils";
import Filter from "sap/ui/model/Filter";
import FilterUtils, { FilterArgument } from "../subtasks/utils/FilterUtils";
import Component from "sap/mcsuite/Component";

export default class BaseTranslator {

    private static REQUEST_URL = "https://code.market-control.eu/intern/modules/CPP/translator.php?key=875013";
    private static CFR_CODE = 105;
    // cache: [type, locale, ...args] => string
    //  get(col1_val1_col2_val2) to get the cached translation
    private cache: Map<string, string> = new Map();
    public isCached: boolean = false;
    protected component: Component;
    protected argKeys: Array<string> = [];
    protected translationTable: string;
    protected translationCache: Map<[String, string], string>;

    constructor(component: Component) {
        this.component = component;
    }



    private buildFilterArgs(argMap: Array<{ key: string; value: string }>): FilterArgument[] {
        return argMap.map(arg => ({
            column: arg.key,
            operator: "EQ",
            value1: arg.value
        }));
    }

    protected async getTranslationOrDefault(...args: string[]): Promise<string> {
        let i = 0;
        const argMap = [];
        let defaultValue : string;
        while (i < this.argKeys.length && i < args.length) {
            argMap.push({
                key: this.argKeys[i],
                value: args[i]
            });
            i++;
        }
        if (i < args.length) {
            defaultValue = args[i];
        }
        const user = this.component.getUser();
        const locale = user.getLocale();
        const cacheKey = this.getCacheKey(...args.slice(0, i));
        // Check if we have a cached translation
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }
        const filters = this.buildFilterArgs(argMap);
        const filter = FilterUtils.buildFilter(filters, true); // Use AND logic for filters
        const translation = await this.getTranslation(locale, filter);
        if (translation) {
            // Cache the translation for future use
            this.cache.set(cacheKey, translation);
            return translation;

        } else {
            // Request translation from backend service
            const backendTranslation = await this.requestTranslationFromBackend(...args);
            if (backendTranslation) {
                this.cache.set(cacheKey, backendTranslation);
                return backendTranslation;
            }
        }
        return defaultValue;
    }

    private async getTranslation(locale: string, filter: Filter): Promise<string | null> {
        const model = this.component.getODataModel();
        const translation = await ODataModelUtils.getFirstMatch(model, '/' + this.translationTable, filter, {
            $select: ['lang_de', 'lang_en', 'lang_es']
        });
        const translationKey = `lang_${locale}`;
        if (!translation) {
            console.warn(`No translation found in table: ${this.translationTable}`);
            return null;
        }
        if (translation[translationKey]) {
            return translation[translationKey] as string;
        } if (translation['lang_en']) {
            console.warn(`Translation not found for locale: ${locale}, using default 'lang_en'`);
            return translation['lang_en'] as string;
        } 
        else {
            return null;
        }
    }

    private getCacheKey(...args: string[]): string {
        args.sort();
        return args.join("_");
    }

    public async fillCache(): Promise<void> {
        const listBinding = this.component.getODataModel().bindList('/' + this.translationTable, undefined, undefined, undefined, {
            $select: this.argKeys.concat(['lang_de']),
            $filter: this.argKeys.map((key) => `${key} ne null`).join(' and ')
        });
        const contexts = await listBinding.requestContexts(0, Infinity);
        await Promise.all(contexts.map(async (context) => {
            const data = await context.requestObject();
            const keyParts = this.argKeys.map(key => data[key]);
            const cacheKey = this.getCacheKey(...keyParts);
            this.cache.set(cacheKey, data['lang_de']);
        }));
        this.isCached = true;
    }
    public clearCache(): void {
        this.cache.clear();
        this.isCached = false;
    }


//     getLabelFromBackend: async function (sType, sName, sDefaultValue = null) {
//       const sURL = `${REQUEST_URL}&code=${CFR_CODE}&cat=2&type=${sType.toUpperCase()}&field=${sName.toUpperCase()}&standard=${sDefaultValue}`;
//       const mHeaders = {
//         "Content-Type": "application/x-www-form-urlencoded",
//         "Accept": "application/json",
//         "Access-Control-Allow-Origin": "*",
//       };
//       let mResponse = {};
//       mResponse = fetch(sURL, {
//         method: "GET",
//         headers: mHeaders,
//         mode: "cors",
//       }).then(
//         function(response) {
          
//           if (response.ok) {
//             return response.text();
//           } else {
//             return null;
//           }
//         }
//       );
//       // read Stream from body;
//       return JSON.parse(await mResponse);
//     }
//     // Communication with CODE
//   };
    private requestTranslationFromBackend(...args: string[]): Promise<string> {
        return new Promise((resolve, reject) => {
            const sType = args[0];
            const sName = args[1];
            const sDefaultValue = args[2] || "";
            const sURL = `${BaseTranslator.REQUEST_URL}&code=${BaseTranslator.CFR_CODE}&cat=2&type=${sType.toUpperCase()}&field=${sName.toUpperCase()}&standard=${sDefaultValue}`;
            const mHeaders = {
                "Content-Type": "application/x-www-form-urlencoded",
                "Accept": "application/json",
                "Access-Control-Allow-Origin": "*",
            };
            fetch(sURL, {
                method: "GET",
                headers: mHeaders,
                mode: "cors",
            }).then(
                function(response) {
                    if (response.ok) {
                        return response.text();
                    } else {
                        return null;
                    }
                }
            ).then(async (data) => {
                const dataObject = await data ? JSON.parse(data) : null;
                if (dataObject["lang_de"]) {
                    // save translation to db and to cache
                    this.cache.set(this.getCacheKey(...args), dataObject["lang_de"]);
                    await this.saveTranslationToDB(args, dataObject);
                    resolve(dataObject["lang_de"]);
                } else {
                    resolve(sDefaultValue);
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }
    private async saveTranslationToDB(args: string[], translationData: { [key: string]: string }): Promise<void> {
        const model = this.component.getODataModel();
        const argMap: { [key: string]: string } = {};
        this.argKeys.forEach((key, index) => {
            argMap[key] = args[index];
        });
        const translationObject = {
            id: -1,
            ...argMap,
            lang_de: translationData["lang_de"],
            lang_en: translationData["lang_en"] || "",
            lang_es: translationData["lang_es"] || ""
        };
        await model.bindList('/' + this.translationTable, undefined, undefined, undefined, {'$$updateGroupId': 'UpdateGroup1'}).create(translationObject);
        await model.submitBatch('UpdateGroup1');
    }
}